import { useEffect, useRef, useState } from 'react';

import { useTrackEvent } from 'analytics/useTrackEvent';

import { AxiosError } from 'axios';
import { Stack, Typography } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';

import { AlertMessage } from 'utilities/utils';
import { parseApiErrorMessage } from 'utilities/error-utils';
import {
  usePutApiV5FilesIdRename,
  getGetApiV5FilesIdNamingSuggestionsQueryKey,
} from '@liscio/api';
import { useGetApiV5FirmsFileNamingTemplates } from '@liscio/api';

import { formatFileSize } from 'components/AI/AIFileNameManagement/components/AIFileNameInfo/utils';

import { useDeterministicNaming } from './hooks';
import {
  DeterministicTitle,
  DeterministicProcessingState,
  DeterministicSuggestionCard,
  DeterministicFileInfo,
} from './components';
import { TemplateConfigModal } from './TemplateConfigModal/TemplateConfigModal';
import { truncateFileName } from './utils';
import { isFirmOnDefaultNamingTemplates } from './utils/firmNamingTemplateStatus';
import { COLORS, TOAST_DURATION } from './constants';


export interface DeterministicFileNameManagementProps {
  fileUuid: string;
  /** Seed for the display name — internal state after mount. Remount via `key` on change. */
  initialFileName?: string;
  fileSize?: number;
  uploadedBy?: string;
  /** Seed for the upload filename — internal state after mount. */
  initialOriginalName?: string;
  /** Seed — internal state after mount. */
  initialAutoNamed?: boolean;
  /** Seed for the AI suggestion — internal state after mount. */
  initialSuggestedName?: string;
  aiIngestionInProgress?: boolean;
  isEmployee?: boolean;
  onFileRenamed?: (displayName: string, downloadUrl?: string) => void;
  /**
   * Fired whenever the displayed filename changes optimistically — when the
   * user clicks Accept (with the suggested/edited name) or saves an inline
   * rename, and again on the reverted name if the mutation rejects. Lets the
   * parent mirror the optimistic value in places this component doesn't own
   * (e.g. the page header). Does NOT replace `onFileRenamed`, which still
   * fires only after the server's canonical response and is the signal for
    * downstream viewer filename + download URL state.
   */
  onOptimisticName?: (name: string) => void;
  /**
   * Fired when the in-flight state of an accept/rename mutation flips. The
   * parent can use this to gate Download buttons that would otherwise carry
   * a stale signed URL during the optimistic window.
   */
  onMutatingChange?: (isMutating: boolean) => void;
  /** Pulse highlight on the suggestion card after onboarding coachmark flyover */
  onboardingSpotlight?: boolean;
}

type MutatedFile = {
  display_name?: string | null;
  original_name?: string | null;
  file_download_url?: string | null;
};

/**
 * Self-contained component for deterministic file naming. Owns its mutable
 * state — seeded from `initial*` props, then updated from API responses.
 * Callers MUST pass `key={fileUuid}` so the component remounts (and re-seeds)
 * when switching to a different document.
 */
export const DeterministicFileNameManagement = ({
  fileUuid,
  initialFileName,
  fileSize,
  uploadedBy,
  initialOriginalName,
  initialAutoNamed,
  initialSuggestedName,
  aiIngestionInProgress,
  isEmployee,
  onFileRenamed,
  onOptimisticName,
  onMutatingChange,
  onboardingSpotlight = false,
}: DeterministicFileNameManagementProps) => {
  const queryClient = useQueryClient();
  const trackEvent = useTrackEvent();
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const onboardingTemplatePromptShownRef = useRef(false);
  const [fileName, setFileName] = useState(initialFileName);
  const [originalName, setOriginalName] = useState(initialOriginalName);
  const [autoNamed, setAutoNamed] = useState(initialAutoNamed ?? false);
  const [suggestedName, setSuggestedName] = useState(initialSuggestedName);
  // Survives the accept-failure unmount of DeterministicSuggestionCard: when
  // an in-flight Accept rejects, we stash the user's typed full name here so
  // the re-mounted suggestion card can seed its EditableFileName back into
  // edit mode with that buffer. Cleared on every stage (so a successful
  // accept followed by some later flow that re-shows the suggestion view —
  // e.g., a template save — doesn't resurrect stale text) and consumed
  // exactly once by the suggestion card on mount via onEditBufferConsumed.
  const [pendingEditBuffer, setPendingEditBuffer] = useState<string | undefined>(undefined);

  const shouldPoll = !autoNamed && !suggestedName && (aiIngestionInProgress ?? false);

  const { data: firmTemplatesData } = useGetApiV5FirmsFileNamingTemplates({
    query: { enabled: onboardingSpotlight },
  });

  useEffect(() => {
    if (
      !onboardingSpotlight
      || onboardingTemplatePromptShownRef.current
      || !firmTemplatesData?.templates
    ) {
      return;
    }

    if (isFirmOnDefaultNamingTemplates(firmTemplatesData.templates)) {
      onboardingTemplatePromptShownRef.current = true;
      setConfigModalOpen(true);
    }
  }, [onboardingSpotlight, firmTemplatesData]);

  const {
    suggestion: polledSuggestion,
    previewValues: polledPreviewValues,
    isProcessing,
    accept,
    isAccepting,
  } = useDeterministicNaming({
    fileUuid,
    enabled: shouldPoll,
  });

  const { mutateAsync: renameMutation, isLoading: isRenaming } = usePutApiV5FilesIdRename();

  // Combined mutation-in-flight signal. `isAccepting` covers the accept path
  // (suggestion → auto-named); `isRenaming` covers the post-accept inline
  // rename. Both feed the optimistic-window indicators (pencil → dot in the
  // panel; dot + Download disable in the parent page).
  const isMutating = isAccepting || isRenaming;

  // Fire onMutatingChange on transitions. Ref-guarded so we only emit when
  // the boolean actually flips — not on every render where the dependency
  // happens to be the same value.
  const prevMutatingRef = useRef(false);
  useEffect(() => {
    if (prevMutatingRef.current === isMutating) return;
    prevMutatingRef.current = isMutating;
    onMutatingChange?.(isMutating);
  }, [isMutating, onMutatingChange]);

  // Ensure the parent's isRenaming flag is cleared if we unmount mid-mutation
  // (e.g. user switches documents — the key={file.uuid} pattern remounts this
  // component, but the parent's isRenaming state persists across remounts).
  // Without this cleanup, the new document would be stuck with the dot + the
  // Download buttons disabled forever. The latest-callback ref defeats the
  // empty-deps useEffect's stale-closure problem.
  const onMutatingChangeRef = useRef(onMutatingChange);
  onMutatingChangeRef.current = onMutatingChange;
  useEffect(() => () => {
    if (prevMutatingRef.current) onMutatingChangeRef.current?.(false);
  }, []);

  const suggestion = suggestedName || polledSuggestion;
  const hasSuggestion = !autoNamed && !!suggestion && suggestion !== fileName;

  // Orval emits query keys as a single-element array whose one element is the
  // full path string (e.g. ['/api/v5/files/abc/naming_suggestions']). React
  // Query's element-by-element prefix match therefore can't invalidate by
  // ['/api/v5/files'] alone — the first elements aren't equal. We instead
  // string-prefix the first key segment and skip firm-wide config endpoints
  // (preferred_format, naming_config) which don't change on a file rename.
  const invalidateFileQueries = () => {
    queryClient.invalidateQueries({
      predicate: (q) => {
        const [firstKey] = q.queryKey;
        if (typeof firstKey !== 'string') return false;
        if (!firstKey.startsWith('/api/v5/files')) return false;
        return !firstKey.startsWith('/api/v5/files/preferred_format')
          && !firstKey.startsWith('/api/v5/files/naming_config');
      },
    });
  };

  const applyFileUpdate = (file: MutatedFile, { markAutoNamed = false } = {}) => {
    if (!file.display_name) {
      // Backend invariant: successful rename/accept always returns display_name.
      // Hitting this branch means the API contract drifted — surface it so the
      // user doesn't see a silent "nothing happened" after the mutation.
      // Invalidate anyway: the server likely persisted the change, so sibling
      // caches (list views etc.) will drift if we skip it.
      AlertMessage('error', 'File renamed but no updated name returned', TOAST_DURATION);
      invalidateFileQueries();
      return;
    }
    const displayName = String(file.display_name);
    setFileName(displayName);
    // Only touch originalName when the response includes the key. The Orval
    // schema marks it optional and nullable — omission is a valid "unchanged"
    // signal, while a null value explicitly clears it.
    if ('original_name' in file) {
      setOriginalName(file.original_name ?? undefined);
    }
    if (markAutoNamed) {
      setAutoNamed(true);
      setSuggestedName(undefined);
    }
    onFileRenamed?.(displayName, file.file_download_url ?? undefined);
    invalidateFileQueries();
  };

  const handleRename = async (newName: string) => {
    // Soft-optimistic: stage the new name before the await so the view-mode
    // filename flips immediately. On rejection, revert. The server response
    // overwrites fileName with the canonical display_name via applyFileUpdate.
    const prevFileName = fileName;
    setFileName(newName);
    onOptimisticName?.(newName);
    try {
      const { file } = await renameMutation({ id: fileUuid, data: { name: newName } });
      // Rename preserves the suggestion-free state (it's a user-driven rename,
      // not an accept); no markAutoNamed here. Trust the backend's response —
      // if display_name is missing, applyFileUpdate surfaces the error.
      applyFileUpdate(file);
    } catch (err) {
      setFileName(prevFileName);
      if (prevFileName) onOptimisticName?.(prevFileName);
      // Surface the server's specific message when present (e.g. the new
      // "file name can't contain '.pdf'" rule) so the user knows what to fix
      // — a generic toast loses that signal.
      AlertMessage('error', parseApiErrorMessage((err as AxiosError)?.response?.data) ?? 'Failed to rename file', TOAST_DURATION);
      // Rethrow so the EditableFileName caller re-opens the editor (its
      // optimisticClose path flips isEditing back to true on rejection).
      throw err;
    }
  };

  const handleAccept = async (
    editedName?: string,
    opts?: { wasEdited?: boolean },
  ) => {
    // Always send the current suggestion explicitly — if the user changed the
    // template, suggestedName holds the new preview but the backend still has
    // the old cached suggestion, so omitting the name would save the stale one.
    const nameToSave = editedName || suggestion || undefined;
    // `wasEdited` distinguishes commit() (edit-mode → true) from acceptAsIs()
    // (view-mode → false). Only the former carries a meaningful typed buffer
    // worth preserving across an accept-failure remount.
    const wasEdited = opts?.wasEdited === true;
    // Soft-optimistic: flip to auto-named view before the await so the
    // suggestion card transitions immediately. applyFileUpdate still runs
    // on success — it overwrites fileName with the canonical display_name
    // and propagates to the parent via onFileRenamed.
    const prev = { fileName, autoNamed, suggestedName };
    if (nameToSave) {
      setFileName(nameToSave);
      onOptimisticName?.(nameToSave);
    }
    setAutoNamed(true);
    setSuggestedName(undefined);
    // Stage-time clear: any pending buffer from a prior failed attempt is no
    // longer relevant — either this attempt succeeds (auto-named view stays),
    // or it fails and we set a fresh buffer below from the *current* edited
    // name.
    setPendingEditBuffer(undefined);
    try {
      const { file } = await accept(nameToSave);
      applyFileUpdate(file, { markAutoNamed: true });
      trackEvent('file_renamed', {
        source: onboardingSpotlight ? 'onboarding' : 'file_intelligence',
        ...(onboardingSpotlight ? { onboarding_step: 'file_renamed' } : {}),
        naming_method: 'deterministic',
      });
    } catch (err) {
      setFileName(prev.fileName);
      setAutoNamed(prev.autoNamed);
      setSuggestedName(prev.suggestedName);
      if (prev.fileName) onOptimisticName?.(prev.fileName);
      // Only stash the buffer when the user was actually editing. The buffer
      // carries the FULL name — EditableFileName splits the extension off when
      // seeding state. View-mode accept (wasEdited=false) has nothing to
      // preserve even though editedName is technically defined as props.value.
      if (wasEdited && editedName) setPendingEditBuffer(editedName);
      AlertMessage('error', parseApiErrorMessage((err as AxiosError)?.response?.data) ?? 'Failed to accept suggestion', TOAST_DURATION);
      throw err;
    }
  };

  const handleTemplateSaved = () => {
    setSuggestedName(undefined);
    setAutoNamed(false);
    // Evict this file's cached suggestion entirely — not just invalidate —
    // so the hook returns null between the old data and the refetched one.
    // Without this, the suggestion card would briefly show (and accept) the
    // pre-template-change value. React Query auto-refetches because the
    // hook's observer is still mounted with enabled=true.
    queryClient.removeQueries({
      queryKey: getGetApiV5FilesIdNamingSuggestionsQueryKey(fileUuid),
    });
  };

  const renderBody = () => {
    if (autoNamed) {
      return (
        <DeterministicFileInfo
          fileName={fileName}
          fileSize={fileSize}
          uploadedBy={uploadedBy}
          originalName={originalName}
          onRename={handleRename}
          isMutating={isMutating}
        />
      );
    }
    if (isProcessing) return <DeterministicProcessingState />;
    if (hasSuggestion && suggestion) {
      return (
        <>
          <DeterministicSuggestionCard
            suggestion={suggestion}
            onAccept={handleAccept}
            isAccepting={isAccepting}
            initialEditBuffer={pendingEditBuffer}
            onEditBufferConsumed={() => setPendingEditBuffer(undefined)}
            onboardingSpotlight={onboardingSpotlight}
          />
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1.25 }}>
            {uploadedBy} {'\u00B7'} {formatFileSize(fileSize)}
          </Typography>
          {fileName && (
            <Typography
              variant="body2"
              color="text.secondary"
              title={fileName}
              sx={{ mt: 1.875, display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <UploadFile sx={{ fontSize: '16px', flexShrink: 0 }} />
              {truncateFileName(fileName)}
            </Typography>
          )}
        </>
      );
    }
    return (
      <DeterministicFileInfo
        fileName={fileName}
        fileSize={fileSize}
        uploadedBy={uploadedBy}
        onRename={handleRename}
        isMutating={isMutating}
      />
    );
  };

  return (
    <Wrapper
      highlighted={hasSuggestion}
      onboardingSpotlight={onboardingSpotlight}
      showOnboardingTarget={hasSuggestion}
      title={hasSuggestion ? 'Suggested file name' : undefined}
      onOpenSettings={isEmployee ? () => setConfigModalOpen(true) : undefined}
      configModalOpen={configModalOpen}
      onCloseSettings={() => setConfigModalOpen(false)}
      previewValues={polledPreviewValues}
      originalName={originalName}
      onTemplateSaved={handleTemplateSaved}
    >
      {renderBody()}
    </Wrapper>
  );
};

const Wrapper = ({
  children,
  onOpenSettings,
  configModalOpen,
  onCloseSettings,
  previewValues,
  originalName,
  onTemplateSaved,
  highlighted = false,
  onboardingSpotlight = false,
  showOnboardingTarget = false,
  title,
}: {
  children: React.ReactNode;
  onOpenSettings?: () => void;
  configModalOpen: boolean;
  onCloseSettings: () => void;
  previewValues?: Record<string, string> | null;
  originalName?: string;
  onTemplateSaved?: () => void;
  highlighted?: boolean;
  onboardingSpotlight?: boolean;
  showOnboardingTarget?: boolean;
  title?: string;
}) => (
  <Stack
    data-onboarding-target={
      showOnboardingTarget ? 'file-rename-suggestion' : undefined
    }
    sx={{
      padding: '16px',
      border: `1px solid ${highlighted ? COLORS.suggestion.cardBorder : '#e9ecef'}`,
      borderRadius: '4px',
      backgroundColor: highlighted ? COLORS.suggestion.cardBg : '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      ...(onboardingSpotlight
        ? {
            animation: 'onboardingFileRenamePulse 2s ease-in-out infinite',
            '@keyframes onboardingFileRenamePulse': {
              '0%, 100%': {
                boxShadow: `0 0 0 3px ${COLORS.suggestion.hoverAccent}`,
              },
              '50%': {
                boxShadow: '0 0 0 6px rgba(66, 165, 245, 0.35)',
              },
            },
          }
        : {}),
    }}
  >
    <DeterministicTitle onOpenSettings={onOpenSettings} title={title} />
    <Typography variant="body2" color="text.disabled" sx={{ mb: 1.25, fontStyle: 'italic' }}>
      Auto file naming based on your firm&apos;s naming templates
    </Typography>
    {children}
    <TemplateConfigModal open={configModalOpen} onClose={onCloseSettings} originalName={originalName} previewValues={previewValues} onTemplateSaved={onTemplateSaved} source={onboardingSpotlight ? 'onboarding' : 'file_intelligence'} />
  </Stack>
);
